import React, { useState, Fragment } from "react";
import { getLocationName } from "./locationUtils";
import { LocationMarkerIcon } from "@heroicons/react/solid";
import { Dialog, Transition } from "@headlessui/react";
import { MdClose } from "react-icons/md";
import Button from "../components/ui/Button";
import PrimaryLoader from "../components/common/PrimaryLoader";

const LocationDisplay = ({ lat, lon }) => {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleFetchAddress = async (e) => {
    e.preventDefault();
    if (address) {
      setIsOpen(true);
      return;
    }
    setLoading(true);
    setIsOpen(true);
    const result = await getLocationName(lat, lon);
    if (result && result.fullAddress) {
      setAddress(result.fullAddress);
    } else {
      setAddress("Unable to fetch location details.");
    }
    setLoading(false);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col items-center justify-center relative">
      <a
        href={`https://www.google.com/maps?q=${lat},${lon}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center justify-center mb-1"
        title="Open in Google Maps"
      >
        <LocationMarkerIcon className="w-6 h-6 cursor-pointer text-[color:var(--color1)] hover:text-black" />
      </a>
      {lat && lon && (
        <button
          onClick={handleFetchAddress}
          className="text-xs text-blue-500 hover:underline focus:outline-none"
        >
          Show Address
        </button>
      )}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[1002]" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between border-b pb-3 mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Location Details
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={closeModal}
                    >
                      <MdClose className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="mt-2">
                    {loading ? (
                      <div className="flex justify-center items-center py-4">
                        <PrimaryLoader />
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        {address}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeModal}
                    >
                      Close
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default LocationDisplay;
